<template>
  <ProfileAvatar
    ref="avatarRef"
    :src="avatar"
    :progress="currentProgress"
    :level="currentLevel"
    :is-viewer="isViewer"
    class="w-full max-w-lg self-center"
  />

  <DialogRoot
    v-model:open="levelUpModalOpen"
    @update:open="
      (v) => {
        if (!v) {
          nextTick(() => (scrollLock = true)).then(() => {
            raf.resume();
            playProgressSound();
          });
        }
      }
    "
  >
    <ProfileDialogContent class="w-full max-w-[375px]" title="Leveled up!">
      <p class="text-center text-sm">You are now</p>
      <p class="my-3 text-center font-Kontesa text-4xl font-bold">
        level {{ currentLevel }}
      </p>

      <p class="text-center text-sm">
        Invite more people into your <br />
        impact family to level up faster!
      </p>

      <ProfileImpactCopyReferralButton
        v-if="referralCode"
        class="mt-6"
        :referral-code="referralCode"
      />
    </ProfileDialogContent>
  </DialogRoot>

  <WrappedModal v-if="isViewer" />

  <Box class="bg-success p-5 @container" rounded="lg">
    <div class="flex flex-col @sm:flex-row">
      <div class="flex-1">
        <div
          class="font-Aeonik text-[28px] font-bold leading-[34px] tracking-tight"
        >
          {{ name }}
        </div>
        <div
          class="font-Aeonik text-[15px] font-medium leading-[18px] tracking-tight"
        >
          {{ city }}, {{ state }} ({{ country }})
        </div>
      </div>
      <div
        class="my-4 w-full border-b border-dashed border-navy @sm:mx-4 @sm:my-0 @sm:w-1 @sm:self-stretch @sm:border-b-0 @sm:border-r"
      />
      <div class="shrink-0 @sm:flex @sm:w-20 @sm:flex-col @sm:text-right">
        <div
          class="inline-block font-Kontesa text-[28px] font-bold leading-none tracking-tight"
          :class="
            previousAmount !== undefined &&
            previousAmount !== amount &&
            currentAmount === amount &&
            'grow-shrink'
          "
        >
          {{ numberFormatter.format(currentAmount) }}
        </div>
        <div
          class="inline-block font-Aeonik text-[15px] font-medium leading-[18px] tracking-tight"
          :class="{
            'grow-shrink':
              initial.level < final.level && currentLevel === final.level,
          }"
        >
          &nbsp;/ {{ numberFormatter.format(toNextLevel) }} IP
        </div>
      </div>
    </div>
  </Box>
</template>

<script setup lang="ts">
import ProfileAvatar from "~/components/Profile/Avatar.vue";
import { getLevelInfo } from "~/lib/levels";
import { scrollIntoView } from "~/lib/dom";
import { useBodyScrollLock } from "radix-vue";

const props = defineProps<{
  name: string;
  city: string;
  state: string;
  country: string;
  avatar?: string;
  impactPoints: number;
  previousImpactPoints?: number;
  isViewer?: boolean;
  referralCode?: string;
}>();
const emit = defineEmits<{
  (e: "animation-end"): void;
}>();

const levelUpModalOpen = ref(false);
watchEffect(() => {
  if (levelUpModalOpen.value) {
    playLevelUpSound();
  }
});

function levelFn(level: number) {
  return Math.ceil(50 * (level ** 0.5 + level));
}

const SPEED = 100 / 3000; // %/ms

const previousAmount = toRef(props, "previousImpactPoints");
const amount = toRef(props, "impactPoints");

const initial = computed(() =>
  getLevelInfo(previousAmount.value ?? amount.value, levelFn),
);
const final = computed(() => getLevelInfo(amount.value, levelFn));

const currentLevel = ref(
  (previousAmount.value ?? 0) > amount.value
    ? final.value.level
    : initial.value.level,
);
const currentProgress = ref(
  (previousAmount.value ?? 0) > amount.value
    ? final.value.progress
    : initial.value.progress,
);
const initialProgress = ref(currentProgress.value);

const currentAmount = ref(
  (previousAmount.value ?? 0) > amount.value
    ? amount.value
    : (previousAmount.value ?? amount.value),
);

const toNextLevel = computed(() => {
  return currentLevel.value === initial.value.level
    ? initial.value.totalExpToNextLevel
    : final.value.totalExpToNextLevel;
});

const duration = computed(() =>
  Math.max(
    800,
    ((final.value.level > currentLevel.value ? 100 : final.value.progress) -
      initialProgress.value) /
      SPEED,
  ),
);
let currentTime = 0;

const raf = useRafFn(
  ({ delta }) => {
    if (initial.value.level > final.value.level) {
      emit("animation-end");
      return;
    }

    const animateLevel = final.value.level > currentLevel.value;
    const progress = animateLevel ? 100 : final.value.progress;
    currentTime += delta;
    const ease = 1 - Math.pow(1 - currentTime / duration.value, 3);
    currentProgress.value = Math.min(
      progress,
      (progress - initialProgress.value) * ease + initialProgress.value,
    );
    if (currentProgress.value === progress) {
      currentTime = 0;
      raf.pause();
      if (animateLevel) {
        nextTick(() => {
          currentLevel.value = final.value.level;
          currentProgress.value = 0;
          initialProgress.value = 0;
          levelUpModalOpen.value = true;
        });
      } else {
        onAnimationEnd();
      }
    }
  },
  { immediate: false },
);

const scrollLock = useBodyScrollLock();
async function playAnimation() {
  currentAmount.value = amount.value;
  initialProgress.value = currentProgress.value;
  if (
    previousAmount.value !== undefined &&
    previousAmount.value !== amount.value &&
    avatarRef.value?.$el
  ) {
    scrollLock.value = true;
    await nextTick();
    await scrollIntoView(avatarRef.value.$el, {
      behavior: "smooth",
      block: "center",
    });
    // Wait a few ms after scroll
    await new Promise((resolve) => setTimeout(resolve, 500));
    playProgressSound();
  }
  raf.resume();
}

function playLevelUpSound() {
  const audio = new Audio("/sounds/level-up.mp3");
  audio.play();
}

function playProgressSound() {
  const audio = new Audio("/sounds/progress-bar.mp3");
  audio.play();
}

async function onAnimationEnd() {
  scrollLock.value = false;
  await nextTick();
  emit("animation-end");
}

const numberFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  notation: "compact",
  maximumFractionDigits: 2,
});

const avatarRef = ref<InstanceType<typeof ProfileAvatar> | null>(null);

defineExpose({
  playAnimation,
});
</script>

<style scoped>
.grow-shrink {
  animation: grow-shrink 600ms;
}

@keyframes grow-shrink {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
